<template>
  <div class="data-import">
    <el-card>
      <div slot="header"><span>数据导入</span></div>
      <el-alert
        title="Excel模板格式说明"
        type="info"
        :closable="false"
        style="margin-bottom:15px">
        <div>Excel列顺序：<b>日期 | 剧目名称 | 剧目ID | 视频号ID | 视频号昵称 | 视频ID | 视频标题 | 是否挂载 | 视频播放量 | 剧集播放量 | 广告收益</b></div>
        <div>第一行为表头（会自动跳过），数据从第二行开始读取</div>
        <div style="margin-top:8px;color:#67c23a">大批量数据（如3万条以上）建议分多个文件导入，每个文件不超过10000条</div>
      </el-alert>
      <el-form inline size="small">
        <el-form-item label="选择剧场" required>
          <el-select v-model="theaterName" placeholder="请先选择剧场" style="width:220px">
            <el-option v-for="t in theaterList" :key="t.id" :label="t.name" :value="t.name"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <el-upload
        ref="upload"
        drag
        action=""
        :auto-upload="false"
        :file-list="fileList"
        :on-change="handleFileChange"
        multiple
        accept=".xlsx,.xls">
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将Excel文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">支持多文件同时导入，只支持 .xlsx / .xls 格式</div>
      </el-upload>
      <el-button type="primary" :loading="importing" :disabled="!theaterName || files.length===0" @click="handleImport" style="margin-top:15px">
        {{ importing ? '导入中...' : '开始导入' }} ({{ files.length }} 个文件)
      </el-button>
      
      <div v-if="importProgress.show" style="margin-top:15px">
        <el-progress :percentage="importProgress.percent" :status="importProgress.status" :text-inside="true" :stroke-width="20"></el-progress>
        <div style="margin-top:8px;color:#666">
          {{ importProgress.message }}
        </div>
      </div>
    </el-card>

    <el-card style="margin-top:20px">
      <div slot="header"><span>导入日志</span></div>
      <el-table :data="logList" border stripe v-loading="logLoading" style="width:100%">
        <el-table-column prop="fileName" label="文件名" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="theaterName" label="剧场" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="successCount" label="成功条数" width="100" align="center"></el-table-column>
        <el-table-column prop="failCount" label="跳过条数" width="100" align="center"></el-table-column>
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === '成功' ? 'success' : scope.row.status === '失败' ? 'danger' : 'info'" size="mini">
              {{ scope.row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="errorMessage" label="错误信息" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column label="导入时间" width="160">
          <template slot-scope="scope">{{ (scope.row.createTime || '').replace('T', ' ') }}</template>
        </el-table-column>
        <el-table-column label="操作" width="70" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" style="color:#f56c6c" @click="handleDeleteLog(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import api from '@/api'
export default {
  name: 'DataImport',
  data() {
    return {
      fileList: [],
      files: [],
      theaterList: [],
      theaterName: '',
      importing: false,
      logList: [],
      logLoading: false,
      importProgress: {
        show: false,
        percent: 0,
        status: 'active',
        message: ''
      },
      batchSize: 500,
      retryDelay: 2000
    }
  },
  mounted() {
    this.loadTheaterList()
    this.loadLogs()
  },
  methods: {
    loadTheaterList() {
      api.theaterList().then(res => { this.theaterList = res.data || [] })
    },
    loadLogs() {
      this.logLoading = true
      api.getImportLogs().then(res => {
        this.logList = res.data || []
      }).finally(() => { this.logLoading = false })
    },
    handleDeleteLog(row) {
      this.$confirm(`确定要删除该次导入的全部数据吗？（${row.fileName}，成功${row.successCount}条）`, '删除确认', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        api.deleteImportLog(row.id).then(() => {
          this.$message.success('已删除导入记录及相关数据')
          this.loadLogs()
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    },
    handleFileChange(file, fileList) {
      this.fileList = fileList
      this.files = fileList.map(f => f.raw)
    },
    async handleImport() {
      if (!this.theaterName) {
        this.$message.warning('请选择剧场')
        return
      }
      if (this.files.length === 0) {
        this.$message.warning('请选择文件')
        return
      }

      this.importing = true
      this.importProgress.show = true
      this.importProgress.percent = 0
      this.importProgress.status = 'active'
      this.importProgress.message = '正在并行上传文件...'

      const totalFiles = this.files.length
      let completedCount = 0
      let successCount = 0
      let failCount = 0

      const maxParallel = 3
      const chunks = []
      for (let i = 0; i < totalFiles; i += maxParallel) {
        chunks.push(this.files.slice(i, i + maxParallel))
      }

      for (const chunk of chunks) {
        const promises = chunk.map(file => {
          const formData = new FormData()
          formData.append('files', file)
          formData.append('theaterName', this.theaterName)
          return this.uploadWithRetry(formData, file.name).then(() => {
            successCount++
            completedCount++
            this.updateProgress(completedCount, totalFiles)
          }).catch(error => {
            failCount++
            completedCount++
            this.updateProgress(completedCount, totalFiles)
            console.error(`导入文件 ${file.name} 失败:`, error)
            this.$message.error(`文件 ${file.name} 导入失败: ${error.message || '未知错误'}`)
          })
        })
        await Promise.all(promises)
      }

      this.importProgress.status = failCount === 0 ? 'success' : 'exception'
      this.importProgress.message = `导入完成！成功: ${successCount} 个文件，失败: ${failCount} 个文件`

      this.$refs.upload.clearFiles()
      this.files = []
      this.fileList = []
      this.loadLogs()

      setTimeout(() => {
        this.importProgress.show = false
        this.importing = false
      }, 3000)
    },
    updateProgress(completed, total) {
      this.importProgress.percent = Math.round((completed / total) * 100)
      this.importProgress.message = `正在导入: ${completed}/${total} 个文件`
    },
    async uploadWithRetry(formData, fileName, retryCount = 0) {
      const maxRetries = 2
      
      try {
        return await api.importExcel(formData)
      } catch (error) {
        if (retryCount < maxRetries) {
          console.warn(`文件 ${fileName} 导入失败，正在重试 ${retryCount + 1}/${maxRetries}...`)
          await this.sleep(this.retryDelay * (retryCount + 1))
          return this.uploadWithRetry(formData, fileName, retryCount + 1)
        }
        throw error
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    }
  }
}
</script>
